import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';
import { syncDailyNoteViewLockFromRemote } from '../services/dailyNoteViewLockRemote';
import {
  DAILY_NOTE_VIEW_LOCK_CHANGED_EVENT,
  DAILY_NOTE_VIEW_SESSION_UNLOCKED_EVENT,
  isDailyNoteViewLockStorageKey,
  isDailyNoteViewSessionUnlocked,
  loadDailyNoteViewLock,
  setDailyNoteViewSessionUnlocked,
  type DailyNoteViewLockChangedDetail,
} from '../utils/dailyNoteViewLockStorage';

function subscribeDailyNoteViewSessionUnlocked(onStoreChange: () => void): () => void {
  window.addEventListener(DAILY_NOTE_VIEW_SESSION_UNLOCKED_EVENT, onStoreChange);
  return () => {
    window.removeEventListener(DAILY_NOTE_VIEW_SESSION_UNLOCKED_EVENT, onStoreChange);
  };
}

/** 데일리 노트 보기 잠금. contentsHidden은 설정 on + 이번 방문에서 아직 암호를 안 연 상태. */
export function useDailyNoteViewLockEnabled(userId: string): {
  enabled: boolean;
  contentsHidden: boolean;
} {
  const uid = userId.trim();
  const { pathname } = useLocation();
  const [enabled, setEnabled] = useState(() => (uid ? loadDailyNoteViewLock(uid).enabled : false));
  const getSessionUnlockedSnapshot = useCallback(
    () => (uid ? isDailyNoteViewSessionUnlocked(uid) : false),
    [uid]
  );
  const sessionUnlocked = useSyncExternalStore(
    subscribeDailyNoteViewSessionUnlocked,
    getSessionUnlockedSnapshot,
    () => false
  );

  useEffect(() => {
    if (!uid) {
      setEnabled(false);
      return undefined;
    }

    setEnabled(loadDailyNoteViewLock(uid).enabled);

    let cancelled = false;
    void syncDailyNoteViewLockFromRemote(uid).then((merged) => {
      if (cancelled) return;
      setEnabled(merged.enabled);
    });

    const syncFromLocal = (nextUserId?: string) => {
      if (nextUserId && nextUserId !== uid) return;
      setEnabled(loadDailyNoteViewLock(uid).enabled);
    };

    const onChanged = (e: Event) => {
      const d = (e as CustomEvent<DailyNoteViewLockChangedDetail>).detail;
      syncFromLocal(d?.userId);
    };
    const onStorage = (e: StorageEvent) => {
      if (!e.key || !isDailyNoteViewLockStorageKey(e.key)) return;
      syncFromLocal();
    };

    window.addEventListener(DAILY_NOTE_VIEW_LOCK_CHANGED_EVENT, onChanged as EventListener);
    window.addEventListener('storage', onStorage);
    return () => {
      cancelled = true;
      window.removeEventListener(DAILY_NOTE_VIEW_LOCK_CHANGED_EVENT, onChanged as EventListener);
      window.removeEventListener('storage', onStorage);
    };
  }, [uid]);

  /** 다른 페이지로 나가면 보기 해제를 버린다. 대시보드는 keep-alive라 경로 변경으로 감지한다. */
  useEffect(() => {
    return () => {
      if (uid) setDailyNoteViewSessionUnlocked(uid, false);
    };
  }, [pathname, uid]);

  return {
    enabled,
    contentsHidden: enabled && !sessionUnlocked,
  };
}
